import translations from "@/lib/translations.json";
import { useWebsiteLang } from "../context/website-lang-context";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  Bell,
  CalendarClock,
  Wallet,
  Receipt,
  Landmark,
  FileCheck,
  Users,
} from "lucide-react";

type ScenarioKey = "shift" | "contract" | "employee";
type Lang = keyof typeof translations;

interface PhoneCountCopy {
  from: number;
  to: number;
  format: string;
}

// What a node shows on the phone screen once the change reaches the mobile app.
interface PhoneCardCopy {
  title: string;
  tag: string;
  day?: string;
  before?: string;
  after?: string;
  count?: PhoneCountCopy;
  meta: string;
}

interface ScenarioCopy {
  tab: string;
  source: { label: string; change: string; phone?: PhoneCardCopy };
  cascade: { label: string; status: string; phone?: PhoneCardCopy }[];
  phoneExtra?: { title: string; text: string; meta: string };
}

interface FlowNodeData {
  icon: React.ElementType;
  label: string;
  badge: string;
  sub?: string;
  color: string;
  phone?: PhoneCardCopy;
}

const scenarioIcons: Record<
  ScenarioKey,
  {
    source: React.ElementType;
    cascade: React.ElementType[];
    sourceColor: string;
    cascadeColors: string[];
  }
> = {
  shift: {
    source: CalendarClock,
    cascade: [Wallet, Landmark],
    sourceColor: "from-teal-500 to-emerald-600",
    cascadeColors: [
      "from-amber-500 to-orange-500",
      "from-emerald-500 to-teal-600",
    ],
  },
  contract: {
    source: FileCheck,
    cascade: [CalendarClock, Receipt, Landmark],
    sourceColor: "from-cyan-500 to-blue-600",
    cascadeColors: [
      "from-teal-500 to-emerald-600",
      "from-orange-500 to-red-500",
      "from-emerald-500 to-teal-600",
    ],
  },
  employee: {
    source: Users,
    cascade: [CalendarClock, Wallet],
    sourceColor: "from-violet-500 to-purple-600",
    cascadeColors: [
      "from-teal-500 to-emerald-600",
      "from-amber-500 to-orange-500",
    ],
  },
};

const KEYS: ScenarioKey[] = ["shift", "contract", "employee"];
const STEP_INTERVAL = 500;
const PHONE_ARROW_DELAY = 400;
const PHONE_UPDATE_DELAY = 1000;
const START_DELAY = 350;

function buildNodes(key: ScenarioKey, copy: ScenarioCopy): FlowNodeData[] {
  const config = scenarioIcons[key];
  return [
    {
      icon: config.source,
      label: copy.source.label,
      badge: copy.source.change,
      sub: copy.source.change,
      color: config.sourceColor,
      phone: copy.source.phone,
    },
    ...copy.cascade.map((item, index) => ({
      icon: config.cascade[index] ?? CalendarClock,
      label: item.label,
      badge: item.status,
      color: config.cascadeColors[index] ?? "from-orange-500 to-red-500",
      phone: item.phone,
    })),
  ];
}

function formatCount(value: number, format: string, lang: Lang) {
  const rounded = Math.round(value);
  if (format === "usd") return `$${rounded}/h`;
  const separator = lang === "es" ? "." : ",";
  return `₡ ${String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, separator)}`;
}

export default function Workflow() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.workflow;
  const [active, setActive] = useState<ScenarioKey>("shift");
  // Number of nodes already lit; 0 means idle.
  const [step, setStep] = useState(0);
  // Node indexes whose arrow towards the phone is lit / whose phone card is updated.
  const [arrows, setArrows] = useState<number[]>([]);
  const [updated, setUpdated] = useState<number[]>([]);
  const [ringing, setRinging] = useState(false);
  const [done, setDone] = useState(false);
  // Bumped on every replay so phone cards restart their counters.
  const [run, setRun] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const hasPlayed = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const scenarioCopy: ScenarioCopy = t.scenarios[active];
  const nodes = buildNodes(active, scenarioCopy);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const at = (ms: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, ms));
  };

  const play = (key: ScenarioKey, delay = 0) => {
    clearTimers();
    setStep(0);
    setArrows([]);
    setUpdated([]);
    setRinging(false);
    setDone(false);
    setRun((r) => r + 1);

    const copy: ScenarioCopy = translations[lang].website.workflow.scenarios[key];
    let time = delay;
    buildNodes(key, copy).forEach((node, i) => {
      at(time, () => setStep(i + 1));
      if (node.phone) {
        at(time + PHONE_ARROW_DELAY, () => setArrows((a) => [...a, i]));
        at(time + PHONE_UPDATE_DELAY, () => {
          setUpdated((u) => [...u, i]);
          setRinging(true);
          at(900, () => setRinging(false));
        });
        time += PHONE_UPDATE_DELAY + 100;
      }
      time += STEP_INTERVAL;
    });
    at(time + 200, () => setDone(true));
  };

  const switchScenario = (key: ScenarioKey) => {
    setActive(key);
    play(key, START_DELAY);
  };

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true;
          play("shift", 700);
          obs.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => {
      obs.disconnect();
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phoneCards = nodes
    .map((node, index) => ({ node, index }))
    .filter(
      (entry): entry is { node: FlowNodeData & { phone: PhoneCardCopy }; index: number } =>
        entry.node.phone !== undefined,
    );

  return (
    <section
      id="workflow"
      ref={sectionRef}
      className="relative overflow-hidden py-20 sm:py-28"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900/50 to-navy-950" />

      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-4 py-1.5 text-xs font-medium text-amber-400 glass">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-slow" />
            {t.badge}
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t.sectionTitlePrefix}{" "}
            <span className="text-gradient-amber">
              {t.sectionTitleHighlight}
            </span>
          </h2>
          <p className="mx-auto max-w-xl text-lg text-white/50">
            {t.sectionSubtitle}
          </p>
        </div>

        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {KEYS.map((key) => (
            <button
              key={key}
              onClick={() => switchScenario(key)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                active === key
                  ? "border-white/25 bg-white/10 text-white"
                  : "border-white/8 bg-transparent text-white/40 hover:border-white/15 hover:text-white/65"
              }`}
            >
              {t.scenarios[key].tab}
            </button>
          ))}
        </div>

        <div
          className="glass cursor-pointer select-none rounded-2xl border border-white/10 p-5 sm:p-10"
          onClick={() => play(active, START_DELAY)}
        >
          <div className="mx-auto grid max-w-lg grid-cols-[minmax(0,1fr)_2.25rem_8.75rem] sm:grid-cols-[minmax(0,1fr)_3.5rem_11rem]">
            {nodes.map((node, i) => (
              <Fragment key={`${active}-${node.label}`}>
                {i > 0 && (
                  <div
                    className="flex justify-center"
                    style={{ gridColumn: 1, gridRow: 2 * i }}
                  >
                    <Connector lit={step >= i + 1} />
                  </div>
                )}
                <div style={{ gridColumn: 1, gridRow: 2 * i + 1 }}>
                  <FlowNode node={node} isSource={i === 0} lit={step >= i + 1} />
                </div>
                {node.phone && (
                  <div
                    className="flex items-center"
                    style={{ gridColumn: 2, gridRow: 2 * i + 1 }}
                  >
                    <PhoneArrow lit={arrows.includes(i)} />
                  </div>
                )}
              </Fragment>
            ))}

            <div
              className="flex"
              style={{ gridColumn: 3, gridRow: `1 / span ${nodes.length * 2 - 1}` }}
            >
              <Phone label={t.phoneLabel} ringing={ringing}>
                {phoneCards.map(({ node, index }) => (
                  <PhoneCard
                    key={`${active}-${run}-${index}`}
                    copy={node.phone}
                    updated={updated.includes(index)}
                    lang={lang}
                  />
                ))}
                {scenarioCopy.phoneExtra && (
                  <StaticPhoneCard copy={scenarioCopy.phoneExtra} />
                )}
              </Phone>
            </div>
          </div>

          <div className="mt-6 flex h-6 items-center justify-center">
            {step === 0 && (
              <p className="text-xs text-white/20">
                {lang === "es"
                  ? "Toca para ver la propagación"
                  : "Tap to see the cascade"}
              </p>
            )}
            {done && (
              <p className="flex items-center gap-1.5 text-xs text-emerald-400/50">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {lang === "es"
                  ? "Sistema sincronizado · toca para repetir"
                  : "System synced · tap to replay"}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FlowNode({
  node,
  isSource,
  lit,
}: {
  node: FlowNodeData;
  isSource: boolean;
  lit: boolean;
}) {
  const Icon = node.icon;
  const litStyle = isSource
    ? "border-2 border-amber-500/60 bg-amber-500/8 shadow-[0_0_28px_rgba(245,158,11,0.18)]"
    : "border border-emerald-500/40 bg-emerald-500/6 shadow-[0_0_22px_rgba(16,185,129,0.12)]";
  const idleStyle = isSource
    ? "border-2 border-white/6 bg-white/[0.02]"
    : "border border-white/5 bg-white/[0.015]";
  const pillStyle = isSource
    ? "border border-amber-500/30 bg-amber-500/15 text-amber-400"
    : "border border-emerald-500/30 bg-emerald-500/15 text-emerald-400";

  return (
    <div
      className={`relative flex w-full items-center gap-3 rounded-xl px-3 py-3 transition-all duration-500 sm:gap-4 sm:px-6 sm:py-4 ${
        lit ? litStyle : idleStyle
      }`}
    >
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-lg transition-all duration-500 sm:h-10 sm:w-10 ${
          lit ? `bg-gradient-to-br ${node.color}` : "bg-white/8"
        }`}
      >
        <Icon
          size={18}
          className={`transition-colors duration-500 ${lit ? "text-white" : "text-white/25"}`}
        />
      </div>
      <div className="min-w-0">
        <p
          className={`text-sm transition-colors duration-500 ${isSource ? "font-bold" : "font-semibold"} ${lit ? "text-white" : "text-white/25"}`}
        >
          {node.label}
        </p>
        {node.sub && (
          <p
            className={`whitespace-nowrap text-xs transition-all duration-500 ${lit ? "text-amber-400" : "text-transparent"}`}
          >
            {node.sub}
          </p>
        )}
      </div>
      {/* The source node already shows its change under the label; repeating it in a pill overlapped it. */}
      {node.badge !== node.sub && (
        <span
          className={`ml-auto hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium transition-all duration-500 sm:inline-block ${
            lit ? `${pillStyle} opacity-100` : "opacity-0"
          }`}
        >
          {node.badge}
        </span>
      )}
    </div>
  );
}

function Connector({ lit }: { lit: boolean }) {
  return (
    <div className="flex flex-col items-center gap-[3px] py-1">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={`h-1.5 w-0.5 rounded-full transition-all duration-300 ${lit ? "bg-amber-500" : "bg-white/8"}`}
          style={{ transitionDelay: lit ? `${i * 60}ms` : "0ms" }}
        />
      ))}
      <div
        className={`h-0 w-0 border-l-[5px] border-r-[5px] border-t-[6px] border-l-transparent border-r-transparent transition-colors duration-300 ${lit ? "border-t-amber-500" : "border-t-white/8"}`}
      />
    </div>
  );
}

// Dashed arrow from a node to the phone, with a dot travelling towards it.
function PhoneArrow({ lit }: { lit: boolean }) {
  return (
    <div className="relative h-5 w-full" aria-hidden="true">
      <div
        className={`absolute inset-x-1 top-[9px] border-t-2 border-dashed transition-colors duration-300 ${lit ? "border-sky-400" : "border-white/10"}`}
      />
      <div
        className={`absolute right-0.5 top-1 h-0 w-0 border-b-[6px] border-l-[8px] border-t-[6px] border-b-transparent border-t-transparent transition-colors duration-300 ${lit ? "border-l-sky-400" : "border-l-white/10"}`}
      />
      {lit && (
        <div className="absolute inset-x-1 top-[5px] h-2.5">
          <span className="animate-workflow-packet absolute top-0 h-2.5 w-2.5 rounded-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,0.9)]" />
        </div>
      )}
    </div>
  );
}

function Phone({
  label,
  ringing,
  children,
}: {
  label: string;
  ringing: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex min-h-[17rem] w-full flex-col gap-2 rounded-3xl border-2 bg-gradient-to-b from-[#0f1a33] to-[#0b1326] p-2 transition-all duration-500 ${
        ringing
          ? "border-sky-400/65 shadow-[0_0_26px_rgba(56,189,248,0.22)]"
          : "border-white/15"
      }`}
    >
      <div className="mx-auto mt-0.5 h-1 w-1/3 rounded bg-white/15" />
      <div className="flex items-center gap-1.5 px-1 text-[10px] font-bold text-white/70">
        <Bell size={13} className="text-sky-400" />
        {label}
      </div>
      <div className="flex flex-1 flex-col gap-2">{children}</div>
      <div className="mt-auto h-1.5 w-[70%] rounded bg-white/6" />
    </div>
  );
}

function PhoneCard({
  copy,
  updated,
  lang,
}: {
  copy: PhoneCardCopy;
  updated: boolean;
  lang: Lang;
}) {
  // Eased progress of the counter; stays 0 until the card is updated.
  const [progress, setProgress] = useState(0);
  const count = copy.count;

  useEffect(() => {
    if (!updated || !count) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / 800);
      setProgress(1 - Math.pow(1 - k, 3));
      if (k < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [updated, count]);

  const before = count
    ? formatCount(count.from, count.format, lang)
    : (copy.before ?? "");
  const value = count
    ? formatCount(
        count.from + (count.to - count.from) * (updated ? progress : 0),
        count.format,
        lang,
      )
    : updated
      ? (copy.after ?? "")
      : before;
  const smallValue = !count && !copy.day;

  return (
    <div
      className={`rounded-xl border p-2.5 transition-all duration-500 ${
        updated
          ? "border-sky-400/45 bg-sky-400/[0.08] shadow-[0_0_18px_rgba(56,189,248,0.16)]"
          : "border-white/8 bg-white/[0.04]"
      }`}
    >
      <div className="flex items-center justify-between gap-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-white/40">
        <span className="min-w-0 truncate">{copy.title}</span>
        <span
          className={`rounded-full bg-sky-400/15 px-1.5 py-px text-[8.5px] font-bold normal-case tracking-normal text-sky-300 transition-all duration-500 ${
            updated ? "scale-100 opacity-100" : "scale-90 opacity-0"
          }`}
        >
          {copy.tag}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5">
        {copy.day && <span className="text-[11px] text-white/55">{copy.day}</span>}
        <span
          className={`font-bold tabular-nums transition-colors duration-500 ${smallValue ? "text-[15px]" : "text-lg"} ${updated ? "text-white" : "text-white/60"}`}
        >
          {value}
        </span>
        <span
          className={`overflow-hidden whitespace-nowrap text-[11px] tabular-nums text-white/35 line-through transition-all duration-500 ${
            updated ? "max-w-24 opacity-100" : "max-w-0 opacity-0"
          }`}
        >
          {before}
        </span>
      </div>
      <p className="mt-1 text-[9.5px] text-white/35">{copy.meta}</p>
    </div>
  );
}

// A phone card that does not change; keeps the screen from looking empty.
function StaticPhoneCard({
  copy,
}: {
  copy: { title: string; text: string; meta: string };
}) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.04] p-2.5">
      <p className="truncate text-[9.5px] font-semibold uppercase tracking-wide text-white/40">
        {copy.title}
      </p>
      <p className="mt-1.5 text-[15px] font-bold text-white/75">{copy.text}</p>
      <p className="mt-1 text-[9.5px] text-white/35">{copy.meta}</p>
    </div>
  );
}
